import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Share } from 'lucide-react';
import { toast } from 'sonner';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
                    || window.navigator.standalone 
                    || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Android install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      const installDismissed = localStorage.getItem('pwa-install-dismissed');
      if (!installDismissed && !standalone) {
        setTimeout(() => setShowPrompt(true), 60000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show prompt if not installed
    if (iOS && !standalone) {
      const installDismissed = localStorage.getItem('pwa-install-dismissed');
      if (!installDismissed) {
        setTimeout(() => setShowPrompt(true), 60000);
      }
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      // For iOS, the prompt stays open to show instructions
      return;
    }

    if (!deferredPrompt) {
      toast.error('Installation not available');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div 
      data-testid="pwa-install-prompt"
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 glass-panel rounded-lg p-4 shadow-lg border border-primary/30 animate-in slide-in-from-bottom"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 p-2 rounded-lg">
          {isIOS ? <Share className="w-6 h-6 text-primary" /> : <Download className="w-6 h-6 text-primary" />}
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-sm mb-1">Install BuzzMap</h3>
          
          {isIOS ? (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                Add to your home screen for the best experience
              </p>
              <div className="bg-black/30 rounded p-3 mb-3">
                <ol className="text-xs text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    <span>Tap the <Share className="w-3 h-3 inline mx-1" /> Share button below</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    <span>Scroll and tap "Add to Home Screen"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    <span>Tap "Add" in the top right</span>
                  </li>
                </ol>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                Add to your home screen for quick access to local events
              </p>
              <Button
                data-testid="install-pwa-btn"
                onClick={handleInstall}
                size="sm"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              >
                Install Now
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
